import { ITeam } from "@gtmhub/teams";
import { IUser, getCurrentUserId } from "@gtmhub/users";
import { toKeyMap } from "@gtmhub/util";
import { UserAssignee } from "@webapp/assignees/models/assignee.models";
import dayjs from "@webapp/shared/libs/dayjs";
import { getLastMondayAsDate } from ".";
import { CheckIn, CheckInAnswer, CheckInNotes, CheckInTemplate } from "../models/checkins-models";
import { ReflectionPanel } from "../models/reflection-panel.models";
import { ReflectionQuestion } from "../models/reflection-question.model";
import {
  ReflectionLabel,
  ReflectionMoodType,
  ReflectionNote,
  ReflectionNoteType,
  noReflectionLabel,
  noteLabelMapping,
  reflectionMoodTypes,
} from "../models/reflections.models";

const defaultCadenceDurationInDays = 7;
const defaultNotes = [
  { id: "", type: "blockers", text: "", isCommentsEnabled: false, reactions: null, comments: null, commentsAssignees: null, commentPendingText: "", gif: { id: "" } },
  { id: "", type: "requests", text: "", isCommentsEnabled: false, reactions: null, comments: null, commentsAssignees: null, commentPendingText: "", gif: { id: "" } },
  { id: "", type: "wins", text: "", isCommentsEnabled: false, reactions: null, comments: null, commentsAssignees: null, commentPendingText: "", gif: { id: "" } },
  { id: "", type: "kudos", text: "", isCommentsEnabled: false, reactions: null, comments: null, commentsAssignees: null, commentPendingText: "", gif: { id: "" } },
  { id: "", type: "takeaways", text: "", isCommentsEnabled: false, reactions: null, comments: null, commentsAssignees: null, commentPendingText: "", gif: { id: "" } },
];

const assignManagerAsTeamMember = (team: ITeam): void => {
  if (!team.manager || !team.managerInfo) {
    return;
  }

  if (!team.members) {
    team.members = [];
    team.membersInfo = [];
  }

  const isAlreadyAssigned = team.members.includes(team.manager) || team.membersInfo.some((m) => m.id === team.managerInfo.id);
  if (isAlreadyAssigned) {
    return;
  }

  team.members.push(team.manager);
  team.membersInfo.push(team.managerInfo);
};

export const mapCheckInResToPanel = (checkInResItems: CheckIn[], team: ITeam, template: CheckInTemplate, startDate: string, expandedId: string): ReflectionPanel[] => {
  assignManagerAsTeamMember(team);

  let checkIns = fillMissingCheckInsWithTemplate(checkInResItems, team, template, startDate);
  if (!checkIns) return;

  checkIns = sortCheckInsByName(checkIns, team.membersInfo);

  checkIns = moveCurrentUserOnTop(checkIns);

  return transformCheckInResToPanel(checkIns, team.membersInfo, template, expandedId);
};

const fillMissingCheckInsWithTemplate = (checkInResItems: CheckIn[], team: ITeam, template: CheckInTemplate, startDate: string): Partial<CheckIn>[] => {
  const membersInfo = team.membersInfo;
  const answers = template.questions.map(
    (question) =>
      ({
        questionId: question.id,
        questionText: question.text,
      } as CheckInAnswer)
  );
  return membersInfo?.map((memberInfo) => {
    const existingCheckIn = checkInResItems.find((item) => item.userId === memberInfo.id);
    const templateCheckIn = {
      accountId: memberInfo.accountId,
      teamId: team.id,
      userId: memberInfo.id,
      templateId: template.id,
      createdAt: startDate,
      answers,
      notesV2: {
        blockers: {
          text: "",
        },
        requests: {
          text: "",
        },
        wins: {
          text: "",
        },
        kudos: {
          text: "",
        },
        takeaways: {
          text: "",
        },
      },
    } as Partial<CheckIn>;

    return existingCheckIn || templateCheckIn;
  });
};

const moveCurrentUserOnTop = (checkIns: Partial<CheckIn>[]): Partial<CheckIn>[] => {
  const currentUserIndex = checkIns.findIndex((checkin) => isCurrentUser(checkin.userId));
  const checksUpdated: Partial<CheckIn>[] = structuredClone(checkIns);
  if (currentUserIndex !== -1) {
    checksUpdated.splice(0, 0, checksUpdated.splice(currentUserIndex, 1)[0]);
  }

  return checksUpdated;
};

const sortCheckInsByName = (checkIns: Partial<CheckIn>[], membersInfo: Partial<IUser>[]): Partial<CheckIn>[] =>
  checkIns.sort((a, b) => {
    const firstUser = findUserById(membersInfo, a);
    const secondUser = findUserById(membersInfo, b);

    return firstUser.name.localeCompare(secondUser.name);
  });

const transformCheckInResToPanel = (checkIns: Partial<CheckIn>[], membersInfo: Partial<IUser>[], template: CheckInTemplate, expandedId: string): ReflectionPanel[] =>
  checkIns.map((checkIn) => {
    const user = findUserById(membersInfo, checkIn);
    const noReflection = !isReflectionAvailable(checkIn);

    return {
      isActive: expandedId === user.id && (!noReflection || isEditable(checkIn)),
      assignee: userToAssignee(user),
      isCurrentUser: isCurrentUser(checkIn.userId),
      isEditing: noReflection && isCurrentUser(checkIn.userId),
      id: checkIn.id,
      labels: getLabels(checkIn),
      mood: template.moodsEnabled || checkIn.settings?.moodsEnabled ? mapMoodResToType(checkIn.mood) : undefined,
      noReflection: noReflection,
      notes: template.notesEnabled || checkIn.settings?.notesEnabled ? mapNotesResToType(checkIn.notesV2) : defaultNotes,
      questions: mapAnswersResToQuestions({ answers: checkIn.answers, template, noReflection }),
      isEditable: isEditable(checkIn),
      // TODO remove dayjs when https://quantive-inc.atlassian.net/browse/GVS-29534 is done
      createdAt: dayjs(checkIn.createdAt).toISOString(),
      settings: checkIn.settings,
      indicators: {
        loadingIndicator: undefined,
      },
    } as ReflectionPanel;
  });

const findUserById = (membersInfo: Partial<IUser>[], checkIn: Partial<CheckIn>): Partial<IUser> => membersInfo.find((memberInfo) => memberInfo.id === checkIn.userId);

const getLabels = (checkin: Partial<CheckIn>): ReflectionLabel[] => {
  if (isReflectionAvailable(checkin) && checkin.settings.notesEnabled) {
    return mapLabelsFromNotesRes(checkin.notesV2);
  }

  if (!isReflectionAvailable(checkin)) {
    return [noReflectionLabel];
  }

  return [];
};

const userToAssignee = (user: Partial<IUser>): UserAssignee => {
  if (!user) return;

  return {
    id: user.id,
    name: user.name,
    picture: user.picture,
    type: "user",
  };
};

const mapMoodResToType = (mood: number): ReflectionMoodType => {
  const moodMap = {
    1: reflectionMoodTypes[0],
    2: reflectionMoodTypes[1],
    3: reflectionMoodTypes[2],
  };

  return moodMap[mood];
};

const mapLabelsFromNotesRes = (notes: CheckInNotes): ReflectionLabel[] => {
  if (!notes) return;

  return Object.keys(notes).flatMap((key: string) => {
    return notes[key]?.text?.length || notes[key]?.gif?.id ? noteLabelMapping[key] : [];
  });
};

const isReflectionAvailable = (reflection: Partial<CheckIn>): boolean => {
  const hasNotesV2 = reflection.notesV2 && Object.values(reflection.notesV2)?.some((note) => note.text || note?.gif?.id);
  return reflection.answers?.some((answer) => answer.text || answer?.gif?.id) || hasNotesV2;
};

const mapNotesResToType = (notesRes: CheckInNotes): ReflectionNote[] => {
  if (!notesRes) return;

  return Object.keys(notesRes).map((noteType: ReflectionNoteType) => ({
    id: notesRes[noteType].id,
    text: notesRes[noteType].text,
    type: noteType,
    gif: notesRes[noteType].gif || { id: "" },
    isCommentsEnabled: false,
    reactions: null,
    comments: null,
    commentsAssignees: null,
    commentPendingText: "",
  }));
};

const mapAnswersResToQuestions = (params: { answers: CheckInAnswer[]; template: CheckInTemplate; noReflection: boolean }): ReflectionQuestion[] => {
  if (!params.answers) return;

  const reflectionUpdatesMap = toKeyMap("id", params.template.questions);

  return params.answers.map((answer) => ({
    id: answer.questionId,
    question: answer.questionText,
    answer: answer.text,
    answerId: answer.id,
    isEnabled: params.noReflection ? reflectionUpdatesMap[answer.questionId]?.isEnabled : true,
    isDefault: reflectionUpdatesMap[answer.questionId]?.isDefault,
    gif: answer.gif || { id: "" },
    isCommentsEnabled: false,
    reactions: null,
    comments: null,
    commentsAssignees: null,
    commentPendingText: "",
  }));
};

const isEditable = (checkIn: Partial<CheckIn>): boolean => {
  return isCurrentUser(checkIn.userId) && isCadenceEditable(checkIn);
};

const isCadenceEditable = (checkIn: Partial<CheckIn>): boolean => {
  const lastMondayAsDate = dayjs(getLastMondayAsDate()).utc();

  const prevCadenceStartDate = dayjs(lastMondayAsDate)
    .subtract(defaultCadenceDurationInDays + 1, "day")
    .add(23, "hours")
    .add(59, "minutes")
    .add(59, "seconds")
    .toISOString();

  const currCadenceEndDate = dayjs(lastMondayAsDate)
    .add(defaultCadenceDurationInDays - 1, "day")
    .add(23, "hours")
    .add(59, "minutes")
    .add(59, "seconds")
    .toISOString();

  return dayjs(checkIn.createdAt).isBetween(prevCadenceStartDate, currCadenceEndDate, "day", "[]");
};

const isCurrentUser = (userId: string): boolean => {
  return userId === getCurrentUserId();
};
